"use client";

import React from "react";
import { ArrowRight } from "lucide-react";
import { Link } from "@/components/common/Link";
import Reveal from "@/components/common/Reveal";
import { useLanguage } from "@/hooks/useLanguage";
import { pick, type Lang } from "@/lib/i18n";
interface ApplicationItem {
  id: string;
  titles: Record<Lang, string>;
  descs: Record<Lang, string>;
  to: string;
}
const applicationsList: ApplicationItem[] = [{
  id: "polymer-filtration",
  titles: {
    en: "Polymer Filtration",
    zh: "聚合物过滤",
    es: "Filtración de polímeros",
    de: "Polymerfiltration",
    fr: "Filtration des polymères",
    pt: "Filtração de polímeros"
  },
  descs: {
    en: "High-pressure melt filtration packs and continuous screen changers for resins and synthetic fibers.",
    zh: "针对熔融树脂与合成纤维挤出工艺的高压熔体过滤网片与连续换网过滤组件。",
    es: "Packs de filtración de masa fundida a alta presión y cambiadores de malla para resinas y fibras sintéticas.",
    de: "Hochdruck-Schmelzefilter und kontinuierliche Siebwechsler für Harze und Synthesefasern.",
    fr: "Packs de filtration de polymères sous haute pression et changeurs de tamis continus pour résines et fibres.",
    pt: "Conjuntos de filtração de polímeros a alta pressão e trocadores contínuos de tela para resinas e fibras."
  },
  to: "/PolymerFiltrationPage"
}, {
  id: "water-liquid-filtration",
  titles: {
    en: "Water & Liquid Filtration",
    zh: "水与液体过滤",
    es: "Filtración de agua y líquidos",
    de: "Wasser- & Flüssigkeitsfiltration",
    fr: "Filtration de l'eau et des liquides",
    pt: "Filtração de água e líquidos"
  },
  descs: {
    en: "Automatic self-cleaning filters, intake strainers and process water clarification media.",
    zh: "全自动自清洗过滤筛网、工业取水过滤网管与工艺水净化分离介质。",
    es: "Filtros autolimpiantes automáticos, filtros de toma y medios de clarificación de agua de proceso.",
    de: "Automatische Rückspülfilter, Einlaufsiebe und Medien zur Prozesswasserklärung.",
    fr: "Filtres autonettoyants automatiques, crépines de prise d'eau et médias de clarification d'eau industrielle.",
    pt: "Filtros autolimpantes automáticos, cestos de captação e meios de clarificação de água de processo."
  },
  to: "/WaterLiquidFiltrationPage"
}, {
  id: "food-pharmaceutical",
  titles: {
    en: "Food & Pharmaceutical Filtration",
    zh: "食品与制药过滤",
    es: "Filtración alimentaria y farmacéutica",
    de: "Lebensmittel- & Pharmafiltration",
    fr: "Filtration agroalimentaire et pharmaceutique",
    pt: "Filtração alimentícia e farmacêutica"
  },
  descs: {
    en: "Hygienic screening mesh and sintered elements for sanitary processing and separation.",
    zh: "符合卫生级处理与分离标准的精细筛分网与多层烧结过滤元件。",
    es: "Mallas sanitarias de tamizado y elementos sinterizados para procesamiento higiénico y separación.",
    de: "Hygienische Siebgewebe und Sinterfilterelemente für sterile Prozesse und Trennungen.",
    fr: "Toiles de tamisage hygiéniques et éléments frittés pour le traitement sanitaire et la séparation.",
    pt: "Telas sanitárias de peneiramento e elementos sinterizados para processamento higiênico e separação."
  },
  to: "/FoodAndPharmaceuticalFiltrationPage"
}, {
  id: "screening-separation",
  titles: {
    en: "Screening & Separation",
    zh: "筛分与分离",
    es: "Tamizado y separación",
    de: "Siebung & Trennung",
    fr: "Criblage et séparation",
    pt: "Peneiramento e separação"
  },
  descs: {
    en: "Industrial vibrating screens, dewatering sieve bends and classification mesh.",
    zh: "工业振动筛分网、脱水弧形筛板与粒度分级筛网。",
    es: "Mallas para cribas vibratorias industriales, tamices curvos de deshidratación y clasificación.",
    de: "Industrielle Vibrationssiebe, Entwässerungs-Bogensiebe und Klassiersiebgewebe.",
    fr: "Tamis vibrants industriels, grilles courbes d'essorage et toiles de classification granulométrique.",
    pt: "Telas vibratórias industriais, peneiras curvas de desaguamento e malhas de classificação."
  },
  to: "/ScreeningAndSeparationHubPage"
}, {
  id: "gas-air-filtration",
  titles: {
    en: "Gas & Air Filtration",
    zh: "气体与空气过滤",
    es: "Filtración de gases y aire",
    de: "Gas- & Luftfiltration",
    fr: "Filtration des gaz et de l'air",
    pt: "Filtração de gases e ar"
  },
  descs: {
    en: "Hot gas particulate retention, mist elimination and compressor protection filters.",
    zh: "高温气体颗粒捕集、除雾除沫器丝网与压缩机进气保护过滤组件。",
    es: "Retención de partículas en gas caliente, eliminación de nieblas y protección de compresores.",
    de: "Heißgas-Partikelabscheidung, Tropfenabscheidung und Kompressorschutzfilter.",
    fr: "Filtration de particules dans les gaz chauds, dévésiculeurs et protection de compresseurs.",
    pt: "Retenção de partículas em gases quentes, eliminação de névoas e proteção de compressores."
  },
  to: "/GasAndAirFiltrationPage"
}, {
  id: "chemical-filtration",
  titles: {
    en: "Chemical Filtration",
    zh: "化学与工艺过滤",
    es: "Filtración química y de procesos",
    de: "Chemische Filtration & Prozessfiltration",
    fr: "Filtration chimique et procédés",
    pt: "Filtração química e de processos"
  },
  descs: {
    en: "Corrosion-resistant alloy meshes for aggressive acids, solvents and catalyst recovery.",
    zh: "适用于腐蚀性酸碱溶剂过滤与催化剂回收的耐蚀合金特种丝网。",
    es: "Mallas de aleación resistentes a la corrosión para ácidos agresivos, disolventes y recuperación de catalizadores.",
    de: "Korrosionsbeständige Legierungsgewebe für aggressive Säuren, Lösungsmittel und Katalysatorrückgewinnung.",
    fr: "Toiles en alliages résistants à la corrosion pour acides agressifs, solvants et récupération de catalyseurs.",
    pt: "Malhas de ligas resistentes à corrosão para ácidos agressivos, solventes e recuperação de catalisadores."
  },
  to: "/ChemicalFiltrationPage"
}, {
  id: "oil-fuel-filtration",
  titles: {
    en: "Oil & Fuel Filtration",
    zh: "油品、燃料及石化过滤",
    es: "Filtración de petróleo y combustibles",
    de: "Öl- & Kraftstofffiltration",
    fr: "Filtration des huiles et carburants",
    pt: "Filtração de óleo e combustíveis"
  },
  descs: {
    en: "High-differential hydraulic strainers, fuel polishing elements and sand control screens.",
    zh: "高差压液压滤芯、燃料净化精密滤片与井下防砂筛管。",
    es: "Filtros hidráulicos de alta presión diferencial, pulido de combustible y control de arena.",
    de: "Hydraulikfilter für hohe Differenzdrücke, Kraftstoffaufbereitung und Sandausschlusssiebe.",
    fr: "Crépines hydrauliques à forte perte de charge, purification de carburant et contrôle des sables.",
    pt: "Filtros hidráulicos para alta pressão diferencial, purificação de combustível e controle de areia."
  },
  to: "/OilFuelFiltrationPage"
}];
export default function Applications() {
  const {
    lang
  } = useLanguage();
  const eyebrow = pick(lang, {
    en: "INDUSTRIAL APPLICATIONS",
    zh: "工业应用领域",
    es: "APLICACIONES INDUSTRIALES",
    de: "INDUSTRIELLE ANWENDUNGEN",
    fr: "APPLICATIONS INDUSTRIELLES",
    pt: "APLICAÇÕES INDUSTRIAIS"
  });
  const heading = pick(lang, {
    en: "Applications",
    zh: "应用领域",
    es: "Aplicaciones",
    de: "Anwendungen",
    fr: "Applications",
    pt: "Aplicações"
  });
  const description = pick(lang, {
    en: "Filtration, screening and separation media engineered for critical industrial processes.",
    zh: "专为关键工业流程打造的精密过滤、筛分与分离介质。",
    es: "Medios de filtración, tamizado y separación diseñados para procesos industriales críticos.",
    de: "Filtrations-, Sieb- und Trennmedien, konstruiert für kritische Industrieprozesse.",
    fr: "Médias de filtration, criblage et séparation conçus pour les procédés industriels critiques.",
    pt: "Meios de filtração, peneiramento e separação projetados para processos industriais críticos."
  });
  const viewAllLabel = pick(lang, {
    en: "View all applications",
    zh: "查看全部应用",
    es: "Ver todas las aplicaciones",
    de: "Alle Anwendungen ansehen",
    fr: "Voir toutes les applications",
    pt: "Ver todas as aplicações"
  });
  return <section id="applicationslist" data-nav="light" className="bg-muted/50 text-foreground mf-section border-b border-border">
      <div className="mf-container">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-10 lg:gap-16 items-start">
          {/* Left Column */}
          <div className="lg:col-span-5 lg:sticky lg:top-28">
            <Reveal>
              <div>
                <span className="text-xs uppercase tracking-widest font-mono text-muted-foreground block mb-3">
                  {eyebrow}
                </span>

                <h2 className="mf-h2 text-foreground">
                  {heading}
                </h2>

                <p className="mt-4 text-base text-muted-foreground leading-relaxed">
                  {description}
                </p>

                <div className="mt-8">
                  <Link to="/ApplicationsPage" className="group inline-flex items-center gap-2 text-sm font-semibold text-foreground hover:text-primary transition-colors">
                    <span>{viewAllLabel}</span>
                    <ArrowRight className="size-4 shrink-0 mf-arrow-nudge text-foreground group-hover:text-primary transition-colors" />
                  </Link>
                </div>
              </div>
            </Reveal>
          </div>

          {/* Right Column: Clean List of Clickable Rows */}
          <div className="lg:col-span-7">
            <div className="border-t border-border">
              {applicationsList.map((item, index) => {
              const title = item.titles[lang] ?? item.titles.en;
              const desc = item.descs[lang] ?? item.descs.en;
              return <Reveal key={item.id} delay={Math.min(index * 60, 300)} className="w-full">
                    <Link to={item.to} className="group block py-5 px-4 sm:px-6 border-b border-border mf-row-hover transition-colors">
                      <div className="flex items-center justify-between gap-4">
                        <div className="min-w-0 flex-1">
                          <h3 className="text-base sm:text-lg font-semibold text-foreground group-hover:text-primary transition-colors">
                            {title}
                          </h3>
                          <p className="mt-1 text-sm text-muted-foreground leading-relaxed">
                            {desc}
                          </p>
                        </div>
                        <ArrowRight className="size-5 shrink-0 text-muted-foreground group-hover:text-primary mf-arrow-nudge transition-colors" />
                      </div>
                    </Link>
                  </Reveal>;
            })}
            </div>
          </div>
        </div>
      </div>
    </section>;
}